import { useAuthContext } from '@/auth/useAuthContext';
import {
  ApiError,
  createGroomer,
  deleteGroomer,
  listGroomers,
  listServices,
  updateGroomer,
} from '@/lib/api';
import type { Facility, Groomer, ServiceCatalogueItem } from '@/lib/types';
import { Plus, ToggleLeft } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * Sub-spec 05 Plan 1 M13 — bare groomer roster scaffolding.
 *
 * List + "Add groomer" modal + deactivate action. Per-groomer profile (with
 * schedule editor + pricing editor) lands in Plan 5; this page just provides
 * the canonical entry surface so operators can create roster rows and the
 * roster is queryable by everything downstream.
 */
export function GroomersListPage() {
  const { state } = useAuthContext();
  const facilities = state.me?.facilitiesInScope ?? [];

  const [groomers, setGroomers] = useState<Groomer[]>([]);
  const [services, setServices] = useState<ServiceCatalogueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [adding, setAdding] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setPermissionDenied(false);
    try {
      const [{ groomers }, svc] = await Promise.all([
        listGroomers(true),
        listServices().catch(() => [] as ServiceCatalogueItem[]),
      ]);
      setGroomers(groomers);
      setServices(svc);
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 403) {
        setPermissionDenied(true);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const displayed = showInactive ? groomers : groomers.filter((g) => g.isActive === 1);

  if (permissionDenied) {
    return (
      <div className="rounded-2xl bg-surface p-12 text-center ring-1 ring-border elevate">
        <p className="text-sm font-medium text-foreground">
          You don't have permission to manage groomers
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          Ask an owner or manager to grant access.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-medium tracking-tight">Groomers</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Roster of groomers who can be assigned to grooming appointments. Click a row to edit
            capable services, per-groomer pricing, and the working schedule.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="size-3.5" strokeWidth={1.75} /> Add groomer
        </button>
      </header>

      <div className="mb-4 flex items-center gap-3 text-sm text-muted-foreground">
        <label className="inline-flex items-center gap-2">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          Show inactive
        </label>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : displayed.length === 0 ? (
        <div className="rounded-2xl bg-surface p-12 text-center ring-1 ring-border">
          <p className="text-sm text-muted-foreground">No groomers yet.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl bg-surface ring-1 ring-border">
          <table className="min-w-[640px] w-full text-left text-sm">
            <thead className="border-b border-border bg-surface-2 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Facilities</th>
                <th className="px-4 py-3 font-medium">Services</th>
                <th className="px-4 py-3 font-medium">Schedule</th>
                <th className="px-4 py-3 font-medium">Active</th>
                <th className="px-4 py-3 font-medium" />
              </tr>
            </thead>
            <tbody>
              {displayed.map((g) => (
                <GroomerRow
                  key={g.id}
                  groomer={g}
                  facilities={facilities}
                  services={services}
                  onChanged={refresh}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {adding && (
        <AddGroomerModal
          facilities={facilities}
          services={services}
          onClose={() => setAdding(false)}
          onCreated={() => {
            setAdding(false);
            void refresh();
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------
interface GroomerRowProps {
  groomer: Groomer;
  facilities: Facility[];
  services: ServiceCatalogueItem[];
  onChanged: () => void;
}

function GroomerRow({ groomer, facilities, services, onChanged }: GroomerRowProps) {
  const [busy, setBusy] = useState(false);

  const workingFacilityIds = (() => {
    try {
      return JSON.parse(groomer.workingFacilityIds) as string[];
    } catch {
      return [];
    }
  })();
  const capable = (() => {
    if (groomer.capableServiceIds === null) return null;
    try {
      return JSON.parse(groomer.capableServiceIds) as string[];
    } catch {
      return [];
    }
  })();
  const facilityLabel = workingFacilityIds
    .map((id) => facilities.find((f) => f.id === id)?.name ?? id)
    .join(', ');
  const serviceLabel =
    capable === null
      ? 'All services'
      : capable.map((id) => services.find((s) => s.id === id)?.name ?? id).join(', ') || '(none)';
  const scheduleLabel = groomer.defaultSchedule ? 'Configured' : 'Not set';

  async function toggleActive() {
    setBusy(true);
    try {
      if (groomer.isActive === 1) {
        await deleteGroomer(groomer.id);
      } else {
        await updateGroomer(groomer.id, { isActive: 1 });
      }
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  return (
    <tr className="border-b border-border last:border-b-0">
      <td className="px-4 py-3 font-medium text-foreground">
        <Link
          to={`/settings/grooming/groomers/${groomer.id}`}
          className="text-foreground underline-offset-2 hover:underline"
          data-testid={`groomer-row-link-${groomer.id}`}
        >
          {groomer.displayName}
        </Link>
      </td>
      <td className="px-4 py-3 text-muted-foreground">{facilityLabel || '—'}</td>
      <td className="px-4 py-3 text-muted-foreground">{serviceLabel}</td>
      <td className="px-4 py-3 text-muted-foreground">{scheduleLabel}</td>
      <td className="px-4 py-3">
        {groomer.isActive === 1 ? (
          <span className="inline-flex items-center rounded-full bg-success/15 px-2 py-0.5 text-xs font-medium text-success">
            Active
          </span>
        ) : (
          <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
            Inactive
          </span>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-2">
          <Link
            to={`/settings/grooming/groomers/${groomer.id}`}
            className="inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-xs text-muted-foreground hover:bg-secondary hover:text-foreground"
          >
            Edit
          </Link>
          <button
            type="button"
            onClick={toggleActive}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-xs text-muted-foreground hover:bg-secondary hover:text-foreground disabled:opacity-50"
          >
            <ToggleLeft className="size-3.5" strokeWidth={1.75} />
            {groomer.isActive === 1 ? 'Deactivate' : 'Reactivate'}
          </button>
        </div>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Add modal
// ---------------------------------------------------------------------------
interface AddModalProps {
  facilities: Facility[];
  services: ServiceCatalogueItem[];
  onClose: () => void;
  onCreated: () => void;
}

function AddGroomerModal({ facilities, services, onClose, onCreated }: AddModalProps) {
  const [displayName, setDisplayName] = useState('');
  const [facilityIds, setFacilityIds] = useState<string[]>(
    facilities.length === 1 && facilities[0] ? [facilities[0].id] : [],
  );
  const [serviceIds, setServiceIds] = useState<string[]>([]);
  const [wildcard, setWildcard] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const groomingServices = services.filter((s) => s.category === 'grooming' && s.isActive === 1);
  const isValid = displayName.trim().length > 0 && facilityIds.length > 0;

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await createGroomer({
        displayName: displayName.trim(),
        workingFacilityIds: facilityIds,
        capableServiceIds: wildcard ? null : serviceIds,
      });
      onCreated();
    } catch (err: unknown) {
      const msg =
        err instanceof ApiError && err.body && typeof err.body === 'object'
          ? ((err.body as { issues?: string[] }).issues?.join('; ') ?? err.code)
          : 'Failed to create groomer';
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-2xl bg-surface p-6 ring-1 ring-border elevate">
        <h2 className="mb-4 font-heading text-xl font-medium">Add groomer</h2>

        <div className="space-y-4">
          <div>
            <label
              htmlFor="groomer-name"
              className="mb-1 block text-xs font-medium uppercase tracking-wider text-muted-foreground"
            >
              Display name
            </label>
            <input
              id="groomer-name"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              placeholder="Alex Smith"
            />
          </div>

          <div>
            <span className="mb-1 block text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Working facilities
            </span>
            <div className="space-y-1">
              {facilities.map((f) => (
                <label key={f.id} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={facilityIds.includes(f.id)}
                    onChange={(e) => {
                      setFacilityIds((prev) =>
                        e.target.checked ? [...prev, f.id] : prev.filter((x) => x !== f.id),
                      );
                    }}
                  />
                  {f.name}
                </label>
              ))}
            </div>
          </div>

          <div>
            <span className="mb-1 block text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Capable services
            </span>
            <label className="mb-2 flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={wildcard}
                onChange={(e) => setWildcard(e.target.checked)}
              />
              All services (wildcard)
            </label>
            {!wildcard && (
              <div className="space-y-1">
                {groomingServices.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    No grooming services configured. Add some on the Services page first.
                  </p>
                ) : (
                  groomingServices.map((s) => (
                    <label key={s.id} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={serviceIds.includes(s.id)}
                        onChange={(e) => {
                          setServiceIds((prev) =>
                            e.target.checked ? [...prev, s.id] : prev.filter((x) => x !== s.id),
                          );
                        }}
                      />
                      {s.name}
                    </label>
                  ))
                )}
              </div>
            )}
          </div>

          {error && <p className="text-sm text-danger">{error}</p>}
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-full px-4 py-2 text-sm text-muted-foreground hover:bg-secondary hover:text-foreground"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!isValid || busy}
            className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {busy ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}
